import type { UINode, UITree } from './contract/uitree.types';

/**
 * Client-side period arithmetic for `periodNav` (M7.5 Phase 2). Lets prev/next compute the adjacent
 * period's params locally so an in-place refresh can re-run the data tool without asking the model to
 * shift the date. Framework-agnostic and pure (no `Date.now()`), so it is trivially testable.
 *
 * The `from` + `period` branch matches the real `get_volumes` MCP tool (`period` = `day|month|year`
 * plus a `from` date); the `periodStart`/`periodEnd` branch is a fallback for range-style tools.
 * Returns the params unchanged if it can't infer the shape (navigation then degrades to a no-op).
 *
 * All date math is done in **UTC** (parse ISO → `setUTC*` → `toISOString`) so the result is identical
 * regardless of the machine timezone — the plan's verbatim helper used local getters/setters, which
 * could roll the day across a DST boundary on a month/year shift.
 */
export function shiftPeriod(params: Record<string, unknown>, direction: 'prev' | 'next'): Record<string, unknown> {
  const d = direction === 'next' ? 1 : -1;
  const toIso = (date: Date): string => date.toISOString().slice(0, 10);

  // Preferred: from + period (get_volumes contract)
  const from = params['from'];
  const period = params['period'];
  if (typeof from === 'string' && typeof period === 'string') {
    const date = new Date(from);
    if (Number.isNaN(date.getTime())) {
      return params;
    }
    if (period === 'day') {
      date.setUTCDate(date.getUTCDate() + d);
    } else if (period === 'month') {
      // Set month and day-of-month=1 atomically so the day never overflows into a neighbouring month.
      date.setUTCMonth(date.getUTCMonth() + d, 1);
    } else if (period === 'year') {
      date.setUTCFullYear(date.getUTCFullYear() + d, 0, 1);
    } else {
      return params;
    }
    return { ...params, from: toIso(date) };
  }

  // Fallback: periodStart/periodEnd — shift by the full duration (inclusive, hence +1 day)
  const start = params['periodStart'];
  const end = params['periodEnd'];
  if (typeof start !== 'string' || typeof end !== 'string') {
    return params;
  }
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return params;
  }
  const duration = endMs - startMs + 86_400_000;
  const delta = d * duration;
  return { ...params, periodStart: toIso(new Date(startMs + delta)), periodEnd: toIso(new Date(endMs + delta)) };
}

/**
 * Derives a Dutch-locale period label from `period`+`from` params so the fast-path refresh can
 * update the `periodNav` label without an LLM turn. Returns `null` when the params do not carry
 * recognisable period information — the caller keeps the existing label in that case.
 */
export function computePeriodLabel(params: Record<string, unknown>): string | null {
  const from = params['from'];
  const period = params['period'];
  if (typeof from !== 'string' || typeof period !== 'string') return null;
  // Parse as UTC midnight to avoid timezone-driven day shifts.
  const date = new Date(`${from}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  if (period === 'month') {
    return new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
  }
  if (period === 'year') {
    return String(date.getUTCFullYear());
  }
  if (period === 'day') {
    return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
  }
  return null;
}

/**
 * Returns a new UITree with every `periodNav` node updated to reflect `newInput` as the new current
 * period: `prevParams`/`nextParams` are recomputed via {@link shiftPeriod} and `label` is updated via
 * {@link computePeriodLabel} (kept unchanged when a label cannot be derived). Called by the fast-path
 * refresh in `CustomerChatClient` so period navigation chains correctly without an LLM round-trip.
 */
export function updatePeriodNavInTree(tree: UITree, newInput: Record<string, unknown>): UITree {
  const prevParams = shiftPeriod(newInput, 'prev');
  const nextParams = shiftPeriod(newInput, 'next');
  const label = computePeriodLabel(newInput);
  return { ...tree, root: patchNode(tree.root, prevParams, nextParams, label) };
}

function patchNode(node: UINode, prevParams: Record<string, unknown>, nextParams: Record<string, unknown>, label: string | null): UINode {
  if (node.type === 'periodNav') {
    return { ...node, prevParams, nextParams, ...(label !== null ? { label } : {}) };
  }
  if (node.type === 'stack' || node.type === 'grid') {
    return { ...node, children: node.children.map((child) => patchNode(child, prevParams, nextParams, label)) };
  }
  return node;
}
